import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import * as idbKeyval from 'idb-keyval';
import { queryClient } from '@/lib/query-client';

const ONE_DAY_MS = 1000 * 60 * 60 * 24;
const STORE_NAME = 'minetech-query-cache';

let started = false;
let persistedStore: idbKeyval.UseStore | null = null;

/**
 * Keys that survive a session reset. The workspace identity is resolved from
 * the hostname, is identical for every visitor and holds no user data, so
 * dropping it leaks nothing — it just left the sign-in screen wearing the
 * platform's own branding until the next full reload.
 */
const SESSION_INDEPENDENT_KEYS = new Set(['tenant-by-host']);

/**
 * Drop every cached query — both in-memory and on disk. Called on login and
 * logout so a session never inherits the previous user's cached responses
 * (role/permission changes would otherwise show stale widgets or hit
 * endpoints the new actor isn't allowed to touch).
 */
export async function resetQueryCache(): Promise<void> {
  queryClient.removeQueries({
    predicate: (query) =>
      !SESSION_INDEPENDENT_KEYS.has(String(query.queryKey[0])),
  });
  queryClient.getMutationCache().clear();
  // Reopen the store by name rather than relying on `persistedStore`: the
  // tenant guard runs before startQueryPersistence(), and the on-disk cache is
  // exactly what it needs to drop.
  const store = persistedStore ?? idbKeyval.createStore(STORE_NAME, 'kv');
  try {
    await idbKeyval.clear(store);
  } catch {
    // best-effort — losing the persisted clear is non-fatal
  }
}

/**
 * Persist the TanStack Query cache to IndexedDB (via idb-keyval) so that read
 * data (incident lists, hierarchy reference, etc.) survives reloads and is
 * available offline on the next launch. IndexedDB beats localStorage here
 * because the cache may exceed localStorage's ~5 MB quota for sites that have
 * been actively browsed for an hour.
 */
export function startQueryPersistence(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  const store = idbKeyval.createStore(STORE_NAME, 'kv');
  persistedStore = store;
  const persister = createAsyncStoragePersister({
    storage: {
      getItem: (key) => idbKeyval.get<string>(key, store).then((v) => v ?? null),
      setItem: (key, value) => idbKeyval.set(key, value, store),
      removeItem: (key) => idbKeyval.del(key, store),
    },
    key: 'minetech-query-cache',
  });

  // 24h gcTime so persisted entries remain reloadable on next boot.
  queryClient.setDefaultOptions({
    queries: {
      gcTime: ONE_DAY_MS,
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: { retry: 0 },
  });

  const buster =
    (import.meta.env['VITE_APP_VERSION'] as string | undefined) ?? 'v1';
  void persistQueryClient({
    queryClient,
    persister,
    maxAge: ONE_DAY_MS,
    buster,
  });
}
